/**
 * @file PickupPointMapDialog.tsx
 * @description Выбор пункта выдачи на карте (phase19.12).
 *
 * Тот же движок, что в разделе «Пункты выдачи»: карта Яндекса по ключу из
 * настроек перевозчика плюс карточка выбранной точки. Отличие одно — здесь
 * точку выбирают, а не просто смотрят.
 */

import { Loader2, Search } from "lucide-react";
import { useEffect, useState } from "react";

import { PickupPointDetails } from "./PickupPointDetails";
import { PickupPointsMap } from "./PickupPointsMap";
import { Alert } from "../ui/alert";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "../ui/sheet";
import { ApiError } from "../../lib/api-client";
import {
  listCarrierPickupPoints,
  type CarrierKind,
  type ShopPickupPointDto,
} from "../../lib/api-shop-carriers";

interface Props {
  open: boolean;
  onClose: () => void;
  carrier: CarrierKind;
  /** Ключ Яндекс.Карт из настроек перевозчика. */
  apiKey: string | null;
  /** Город, с которого открываем карту (обычно — город отправления). */
  initialCity?: string;
  onPick: (point: ShopPickupPointDto) => void;
}

export function PickupPointMapDialog({
  open,
  onClose,
  carrier,
  apiKey,
  initialCity,
  onPick,
}: Props) {
  const [city, setCity] = useState(initialCity ?? "");
  const [points, setPoints] = useState<ShopPickupPointDto[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);

  useEffect(() => {
    if (open) setCity(initialCity ?? "");
  }, [open, initialCity]);

  // Кэш ПВЗ большой, поэтому грузим по городу и с задержкой на ввод.
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    const timer = setTimeout(() => {
      setLoading(true);
      setError(null);
      void listCarrierPickupPoints(carrier, { city: city.trim() || undefined })
        .then((data) => {
          if (!cancelled) setPoints(data);
        })
        .catch((err: unknown) => {
          if (!cancelled) {
            setError(err instanceof ApiError ? err.message : "Не удалось загрузить пункты");
          }
        })
        .finally(() => {
          if (!cancelled) setLoading(false);
        });
    }, 400);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [open, carrier, city]);

  const selected = points.find((p) => p.id === selectedId) ?? null;

  return (
    <Sheet open={open} onOpenChange={(o) => !o && onClose()}>
      <SheetContent side="right" className="w-full max-w-none gap-0 md:max-w-3xl">
        <SheetHeader className="shrink-0">
          <SheetTitle>Пункт сдачи на карте</SheetTitle>
          <SheetDescription>
            Выберите пункт, куда вы сами привозите посылки для СДЭК.
          </SheetDescription>
        </SheetHeader>

        <div className="-mx-6 flex-1 overflow-y-auto px-6 py-4">
          <div className="flex flex-col gap-3">
            <div className="relative">
              <Search className="absolute left-2.5 top-2.5 size-4 text-on-surface-variant" />
              <Input
                className="pl-8"
                placeholder="Город (например, Челябинск)"
                value={city}
                onChange={(e) => setCity(e.target.value)}
              />
            </div>

            {error ? <Alert severity="error">{error}</Alert> : null}
            {loading ? (
              <div className="flex items-center gap-2 text-sm text-on-surface-variant">
                <Loader2 className="size-4 animate-spin" /> Загружаем пункты…
              </div>
            ) : null}

            <PickupPointsMap
              points={points}
              apiKey={apiKey}
              selectedId={selectedId}
              onSelect={(p) => setSelectedId(p.id)}
            />

            {selected ? (
              <div className="rounded-lg p-3 [box-shadow:inset_0_0_0_1px_var(--outline-variant)]">
                <PickupPointDetails point={selected} />
              </div>
            ) : (
              <p className="text-sm text-on-surface-variant">
                Нажмите метку на карте, чтобы выбрать пункт.
              </p>
            )}
          </div>
        </div>

        <div className="sticky bottom-0 flex shrink-0 flex-col gap-2 border-t border-outline-variant bg-surface-lowest/95 pt-3 backdrop-blur sm:flex-row sm:justify-end">
          <Button variant="ghost" className="min-h-11" onClick={onClose}>
            Отмена
          </Button>
          <Button
            className="min-h-11"
            disabled={!selected}
            onClick={() => {
              if (!selected) return;
              onPick(selected);
              onClose();
            }}
          >
            Выбрать этот пункт
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
}
